import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { artistsList } from '@/mocks/artists';

export default function ArtistsSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const featured = artistsList.slice(0, 8);

  return (
    <section ref={ref} className="section-padding bg-[#080808] relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-[#d4af37]/20 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className={`flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-[#d4af37]"></span>
              <span className="text-[#d4af37] text-xs font-semibold tracking-[3px] uppercase">Our Roster</span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white">
              Featured <span className="text-[#d4af37]">Artists</span>
            </h2>
            <p className="text-white/60 mt-3 max-w-lg">
              Legends, icons, and rising stars — the voices that define Ganga Digital's musical legacy.
            </p>
          </div>
          <Link to="/artists" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-[#d4af37] whitespace-nowrap border border-[#d4af37]/40 hover:border-[#d4af37] hover:bg-[#d4af37]/10 transition-all cursor-pointer flex-shrink-0">
            View All Artists <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6">
          {featured.map((artist, i) => (
            <Link
              key={artist.id}
              to="/artists"
              className={`group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  alt={`${artist.name} - Ganga Digital artist`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                  src={artist.img}
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent"></div>
              <div className="absolute inset-0 bg-[#d4af37]/0 group-hover:bg-[#d4af37]/10 transition-all duration-300"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="text-white font-semibold text-sm font-heading">{artist.name}</div>
                <div className="text-[#d4af37] text-xs mt-0.5">{artist.genre}</div>
              </div>
              <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-[#d4af37]/0 group-hover:bg-[#d4af37] transition-all duration-300">
                <i className="ri-arrow-right-up-line text-black text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
              </div>
            </Link>
          ))}
        </div>

        <div className={`text-center mt-10 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <Link to="/artists" className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold text-black whitespace-nowrap btn-gold cursor-pointer transition-all hover:scale-105">
            Explore All 29 Artists <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
